import {
  ALL_FACES,
  DEFAULT_CONSTANTS,
  FILES,
  SUPERSTITIONS,
  baseDeckSpec,
  cardSpecIndex,
  hex64,
  makeConfig,
  type CardSpec,
  type CarvingSpec,
  type Constants,
  type Epitaph,
  type HouseRuleSpec,
  type ItemSpec,
  type KitSpec,
  type MatchEvent,
  type MatchState,
  type RouteStep,
  type OmenKind,
  type OmenSpec,
  type PawnSpec,
  type ReserveSpec,
  type SuperstitionId,
} from '../../engine';
import {
  CampaignError,
  applyRecords,
  clone,
  emptyRecords,
  isNightActive,
  isRunActive,
  recruitInto,
  remove,
} from '../campaign';
import type { FieldedPawn, Profile, RosterEntry } from '../types';
import {
  BROKER_RULES,
  ECONOMY_SUPERSTITIONS,
  FAVORS,
  FIXTURE_PAIRS,
  LONG_NIGHT,
  MOURNING_ROUTES,
  PATRONS,
  PATRON_QUIRKS,
  PLACEABLE_OMENS,
  PLEDGES,
  REGULAR_KITS,
  RULE_POOL,
  SUPERSTITION_INFO,
  type FavorId,
  type PledgeId,
} from './catalog';
import { checkUnlocks, noteStats } from './meta';
import { openCounter } from './shop';
import { enterSpoils } from './detours';
import type {
  LedgerProfile,
  NightMatch,
  NightOutcome,
  NightRun,
  NightTable,
  TableKind,
} from './types';
import { ledgerOf, mantelSlots, nightRunOf, nextUid, owns, rngFor } from './util';

/**
 * The Long Night (docs/SHOP_DESIGN.md §5): runs of three Nights of three tables, Closing Time
 * against the Broker, and After Hours. Like the classic campaign it builds match configs and
 * consumes match results; every operation returns a new profile.
 */

export const LEDGER_PROFILE_VERSION = 1;

const emptyStats = (): LedgerProfile['stats'] => ({
  runs: 0,
  wins: 0,
  bestChain: 0,
  deepestNight: 0,
  ladders: 0,
  fullCircles: 0,
  crownsInATable: 0,
  capturesInARound: 0,
  epitaphReplays: 0,
  omensLanded: 0,
  mostBrass: 0,
  chancesHit: 0,
  debts: 0,
  streaks: 0,
  patternsScored: 0,
  monochromeRounds: 0,
  pawnedInATable: 0,
});

/** A profile with its Long Night part, created on first use. */
export function withLedger(profile: Profile): Profile {
  if (profile.ledger) return profile;
  const p = clone(profile);
  p.ledger = {
    version: LEDGER_PROFILE_VERSION,
    rng: rngFor({ seed: profile.seed }, 'ledger').toState(),
    run: null,
    seen: [],
    unlocked: [],
    terms: {},
    hallmarks: {},
    formerCarvings: {},
    heirlooms: [],
    stats: emptyStats(),
    history: [],
    daily: {},
    hallmarkPending: false,
  };
  return p;
}

// ---------------------------------------------------------------------------------------------
// Pledges and Terms
// ---------------------------------------------------------------------------------------------

/** Pledges the player may choose: the Pocket Watch, and every one unlocked since. */
export function availablePledges(l: LedgerProfile): PledgeId[] {
  return PLEDGES.filter((id) => id === 'pocket-watch' || l.unlocked.includes(`pledge:${id}`));
}

/** The highest Term a Pledge may be played at: one above the highest won, once Terms unlock. */
export function maxTerm(l: LedgerProfile, pledge: PledgeId): number {
  if (!l.unlocked.includes('terms')) return 1;
  return Math.min(8, (l.terms[pledge] ?? 0) + 1);
}

/** The run deck a Pledge starts with (docs/SHOP_DESIGN.md §12.1). */
export function pledgeDeck(
  pledge: PledgeId,
  rng: () => number,
  constants: Constants = DEFAULT_CONSTANTS,
): CardSpec[] {
  const base = baseDeckSpec(constants);
  const wild = (): CardSpec => ({ kind: 'wild' });
  switch (pledge) {
    case 'wedding-ring':
      return base.filter((c) => c.kind !== 'wild');
    case 'fiddle': {
      const out: CardSpec[] = [];
      for (const face of ALL_FACES.filter((f) => f.color === 'R' || f.color === 'B'))
        for (let i = 0; i < 4; i++) out.push({ kind: 'normal', face });
      return [...out, wild(), wild()];
    }
    case 'ivory-set':
      return base.filter((c) => c.kind === 'wild' || c.face.color !== 'Y');
    case 'bare-box':
      return base.filter((c) => c.kind === 'wild' || c.face.number !== 3);
    case 'shuffled-box':
      return Array.from({ length: base.length }, () => {
        const k = rng() % 13;
        return k === 12 ? wild() : { kind: 'normal' as const, face: ALL_FACES[k]! };
      });
    default:
      return base;
  }
}

// ---------------------------------------------------------------------------------------------
// Nights and tables
// ---------------------------------------------------------------------------------------------

const TABLE_KINDS: readonly TableKind[] = ['small', 'big', 'high'];

/** The bosses' powers at After Hours High Tables, in the order they come (LEDGER.MD §13.1). */
const AFTER_HOURS_HORRORS = [
  'storm',
  'switch',
  'lights-out',
  'turned-table',
  'clean-slate',
  'blind-eye',
] as const satisfies readonly HouseRuleSpec['id'][];

/** The head start of a table, after Terms, Bargains and the Scale Pledge. */
export function anteFor(run: NightRun, night: number, index: number): number {
  let base: number;
  if (night <= LONG_NIGHT.NIGHTS) base = LONG_NIGHT.ANTES[night - 1]![index]!;
  else if (night === LONG_NIGHT.NIGHTS + 1) base = LONG_NIGHT.ANTE_CLOSING;
  else base = LONG_NIGHT.ANTE_CLOSING * LONG_NIGHT.ANTE_GROWTH ** (3 * (night - 5) + index + 1);
  let a = base * (run.term >= 3 ? 1.3 : 1) * run.anteFactor;
  if (run.pledge === 'scale') a *= 2;
  return Math.round(a);
}

/** Omens for a table: point-symmetric pairs on ranks 2–5, a few of them Shrouded. */
function tableOmens(
  run: NightRun,
  night: number,
  index: number,
  fog: boolean,
  kinds?: readonly OmenKind[],
): OmenSpec[] {
  if (!run.counters.omensUnlocked) return [];
  const rng = rngFor(run, `omens:${night}:${index}`);
  const perNight = LONG_NIGHT.OMENS_PER_NIGHT[Math.min(2, night - 1)] ?? 4;
  const count = perNight * (run.pledge === 'glass-eye' ? 2 : 1);
  const out: OmenSpec[] = [];
  const used = new Set<string>();
  const pool: readonly OmenKind[] = [
    ...PLACEABLE_OMENS,
    'thorns',
    'draft',
    'mimic',
    'looking-glass',
    'brazier',
  ];
  for (let i = 0; out.length < count && i < 40; i++) {
    const x = rng.nextBelow(6);
    const y = 1 + rng.nextBelow(2);
    const kind =
      kinds?.[out.length % kinds.length] ?? (pool[rng.nextBelow(pool.length)] as OmenKind);
    const a = { x, y };
    const b = { x: 5 - x, y: 5 - y };
    const keyA = `${a.x},${a.y}`;
    const keyB = `${b.x},${b.y}`;
    if (used.has(keyA) || used.has(keyB)) continue;
    used.add(keyA);
    used.add(keyB);
    const shroud = kind !== 'pillar' && (fog || (!owns(run, 'oil-lamp') && rng.nextBelow(4) === 0));
    out.push({ kind, square: a, ...(shroud ? { shrouded: true } : {}) });
    if (out.length < count) out.push({ kind, square: b, ...(shroud ? { shrouded: true } : {}) });
  }
  return out;
}

/** Draws the Fixture that waits under the dome this Night. */
function drawFixture(run: NightRun, l: LedgerProfile): string | null {
  const rng = rngFor(run, `fixture:${run.night}`);
  const gated = (id: string) =>
    (id === 'coat-hook' && !l.unlocked.includes('curios')) ||
    (id === 'carvers-bench' && !l.unlocked.includes('carvings')) ||
    (id === 'oil-lamp' && !l.unlocked.includes('carvings')) ||
    (id === 'grave-plot' && !l.unlocked.includes('carvings'));
  const candidates = FIXTURE_PAIRS.flatMap(([a, b]) =>
    !owns(run, a) ? (gated(a) ? [] : [a]) : !owns(run, b) ? [b] : [],
  );
  if (candidates.length === 0) return null;
  return candidates[rng.nextBelow(candidates.length)] ?? null;
}

/** Sets up a Night's board (docs/SHOP_DESIGN.md §5.3). */
export function enterNight(run: NightRun, night: number, l: LedgerProfile): void {
  run.night = night;
  run.table = 0;
  run.counters.omensUnlocked = l.unlocked.includes('omens') ? 1 : 0;
  run.counters.tricksUnlocked = l.unlocked.includes('tricks') ? 1 : 0;
  const term = run.term;
  const chosenOmens = run.chartedOmens;
  if (night === LONG_NIGHT.NIGHTS + 1) {
    // At Closing Time the Broker blinds one of your eyes (LEDGER.MD §13.1), besides his rule.
    const rules: HouseRuleSpec[] = [
      ...(run.brokerRule === 'final-appraisal' ? [] : [{ id: run.brokerRule }]),
      { id: 'blind-eye' },
    ];
    run.board = [
      {
        kind: 'closing',
        ante: anteFor(run, night, 0),
        rules,
        omens: tableOmens(run, night, 0, false, chosenOmens),
        status: 'open',
      },
    ];
  } else {
    const after = night > LONG_NIGHT.NIGHTS + 1;
    run.board = TABLE_KINDS.map((kind, index) => {
      const rng = rngFor(run, `table:${night}:${index}`);
      if (kind !== 'high') {
        const patron = PATRONS[rng.nextBelow(PATRONS.length)]!;
        const favor = FAVORS[rng.nextBelow(FAVORS.length)] as FavorId;
        const quirk = term >= 2 ? PATRON_QUIRKS[patron] : undefined;
        const rules: HouseRuleSpec[] = [...(quirk?.rules ?? [])];
        if (after && night >= 7)
          rules.push({ id: RULE_POOL[rng.nextBelow(RULE_POOL.length)]!, ...tarnishColor(rng) });
        const omens = tableOmens(run, night, index, false, chosenOmens);
        return {
          kind,
          ante: anteFor(run, night, index),
          patron,
          favor,
          rules,
          omens,
          status: 'open',
        };
      }
      const boss = after
        ? { regular: rng.nextBelow(3), rule: REGULAR_KITS[0]!.rules[0]! }
        : run.bosses[night - 1]!;
      const regular = after ? rng.nextBelow(3) : boss.regular;
      const rule = after ? REGULAR_KITS[regular]!.rules[rng.nextBelow(3)]! : boss.rule;
      const rules: HouseRuleSpec[] = [{ id: rule, ...(rule === 'pickpocket' ? { n: 2 } : {}) }];
      // After Hours: every High Table's boss brings one of the powers, each in turn.
      if (after) rules.push({ id: AFTER_HOURS_HORRORS[(night - LONG_NIGHT.NIGHTS - 2) % 6]! });
      if (term >= 6 || (after && night >= 6))
        rules.push({ id: RULE_POOL[rng.nextBelow(RULE_POOL.length)]!, ...tarnishColor(rng) });
      if (run.stolenGoods && regular === 1) rules.push({ id: 'gleam' });
      return {
        kind,
        ante: anteFor(run, night, index),
        regular,
        rules,
        omens: tableOmens(run, night, index, false, chosenOmens),
        status: 'open',
      };
    });
  }
  delete run.chartedOmens;
  run.nightFixture = drawFixture(run, l);
  run.phase = 'board';
  l.stats.deepestNight = Math.max(l.stats.deepestNight, night);
}

function tarnishColor(rng: ReturnType<typeof rngFor>): { color?: 'R' | 'B' | 'G' | 'Y' } {
  return { color: (['R', 'B', 'G', 'Y'] as const)[rng.nextBelow(4)] };
}

export const currentTable = (run: NightRun): NightTable => {
  const t = run.board[run.table];
  if (!t) throw new CampaignError('no table is waiting');
  return t;
};

// ---------------------------------------------------------------------------------------------
// Starting a run
// ---------------------------------------------------------------------------------------------

export interface NightRunOptions {
  readonly pledge: PledgeId;
  readonly term: number;
  /** An Heirloom to bring: it starts the first table in the Epitaph slot. */
  readonly heirloom?: string;
  /** A Daily Ledger date (YYYY-MM-DD): the seed and Pledge are the day's. */
  readonly daily?: string;
  /**
   * A run seed typed in or shared (docs/SHOP_DESIGN.md §14.7): the same seed deals the same
   * Nights, tables and Counter rolls, given the same unlocks.
   */
  readonly seed?: string;
}

/** The Daily Ledger's Pledge for a date: the same for everyone. */
export function dailyPledge(date: string): PledgeId {
  const rng = rngFor({ seed: `daily-${date}` }, 'pledge');
  const list = PLEDGES.filter((p) => p !== 'ash' && p !== 'shuffled-box');
  return list[rng.nextBelow(list.length)]!;
}

/** Starts a Long Night run with pawns chosen from the stable (§15.2 rules for the roster). */
export function startNightRun(
  profile: Profile,
  chosen: readonly string[],
  opts: NightRunOptions,
  constants: Constants = DEFAULT_CONSTANTS,
): Profile {
  if (isRunActive(profile) || isNightActive(profile))
    throw new CampaignError('a run is already in progress');
  const p = withLedger(clone(profile));
  const l = ledgerOf(p);
  if (l.run) finishNightRunInto(l, false);
  const pledge = opts.daily ? dailyPledge(opts.daily) : opts.pledge;
  const term = opts.daily ? 3 : opts.term;
  if (!opts.daily && !availablePledges(l).includes(pledge))
    throw new CampaignError('that Pledge is still locked');
  if (!opts.daily && (term < 1 || term > maxTerm(l, pledge)))
    throw new CampaignError('that Term is not open yet');
  const size = constants.CAMPAIGN_FIELDED;
  const needed = Math.min(size, p.stable.length);
  if (new Set(chosen).size !== chosen.length) throw new CampaignError('a pawn was chosen twice');
  if (chosen.length !== needed)
    throw new CampaignError(`choose exactly ${needed} pawns from the stable`);
  for (const id of chosen)
    if (!p.stable.includes(id)) throw new CampaignError(`${id} is not in the stable`);
  const roster: RosterEntry[] = chosen.map((id) => ({ id, standIn: false }));
  let standInSeq = 0;
  while (roster.length < size) roster.push({ id: `SI-${++standInSeq}`, standIn: true });

  l.stats.runs += 1;
  const rngSeed = rngFor({ seed: `${p.seed}:${l.stats.runs}` }, 'run');
  const typed = opts.seed?.trim().slice(0, 64);
  const seed = opts.daily
    ? `daily-${opts.daily}`
    : typed
      ? typed
      : `night-${hex64(rngSeed.nextU64())}`;
  const draw = rngFor({ seed }, 'deck');
  const run: NightRun = {
    number: l.stats.runs,
    seed,
    pledge,
    term,
    ...(opts.daily ? { daily: opts.daily } : {}),
    night: 1,
    table: 0,
    phase: 'board',
    board: [],
    roster,
    standInSeq,
    fielding: roster.slice(0, 6).map((r) => r.id),
    brass: pledge === 'pocket-watch' ? 8 : pledge === 'iou' ? -10 : LONG_NIGHT.BRASS_START,
    deck: pledgeDeck(pledge, () => draw.nextU32(), constants),
    mantel: [],
    curios: [],
    appraisals: {},
    discovered: [],
    fixtures: [],
    favors: [],
    counters: {},
    carvings: {},
    bosses: (() => {
      const rng = rngFor({ seed }, 'bosses');
      const order = rng.shuffle([0, 1, 2]);
      return order.map((regular) => ({
        regular,
        rule: REGULAR_KITS[regular]!.rules[rng.nextBelow(3)]!,
      }));
    })(),
    brokerRule: BROKER_RULES[rngFor({ seed }, 'broker').nextBelow(BROKER_RULES.length)]!,
    nightFixture: null,
    shop: null,
    spoils: null,
    detour: null,
    match: null,
    outcomes: [],
    pendingOmens: [],
    epitaphs: [],
    bestInscriptions: {},
    uid: 0,
    bestChain: 0,
    anteFactor: 1,
    extraSlots: pledge === 'iou' ? 1 : 0,
    stolenGoods: false,
  };
  // The Pledge's kit.
  const give = (id: SuperstitionId, extra: Partial<ItemSpec> = {}) =>
    run.mantel.push({ uid: nextUid(run), id, ...extra });
  if (pledge === 'wedding-ring') give('widows-veil');
  if (pledge === 'fiddle') give('ribbon');
  if (pledge === 'locket') give('keepsake-locket');
  if (pledge === 'dice') {
    give('loaded-die');
    give('lucky-coin');
  }
  if (pledge === 'ash') {
    const rares = SUPERSTITIONS.filter((id) => SUPERSTITION_INFO[id].rarity === 'rare');
    give(rares[rngFor({ seed }, 'ash').nextBelow(rares.length)]!);
    run.counters.rotting = 1;
  }
  if (pledge === 'glass-eye') run.fixtures.push('oil-lamp');
  if (pledge === 'iou') run.fixtures.push('ledger');
  if (pledge === 'ivory-set') for (const r of roster) run.carvings[r.id] = [{ id: 'spurs' }];
  // Hallmarks: Carvings made permanent on named pawns.
  for (const r of roster) {
    const h = l.hallmarks[r.id];
    if (h && !(run.carvings[r.id] ?? []).some((c) => c.id === h.id))
      run.carvings[r.id] = [...(run.carvings[r.id] ?? []), { ...h }];
  }
  if (opts.heirloom) {
    const h = l.heirlooms.find((x) => x.id === opts.heirloom);
    if (!h) throw new CampaignError('unknown Heirloom');
    run.epitaphs.push({
      id: `H-${h.id}`,
      owner: 'S',
      sourcePawnId: h.pawnId,
      inscription: h.inscription,
    });
  }
  if (pledge === 'locket' && run.epitaphs.length === 0 && l.heirlooms.length > 0) {
    const h = l.heirlooms[l.heirlooms.length - 1]!;
    run.epitaphs.push({
      id: `H-${h.id}`,
      owner: 'S',
      sourcePawnId: h.pawnId,
      inscription: h.inscription,
    });
  }
  l.run = run;
  enterNight(run, 1, l);
  return p;
}

// ---------------------------------------------------------------------------------------------
// Skipping and fielding
// ---------------------------------------------------------------------------------------------

/** Applies a Favor taken for skipping a table (docs/SHOP_DESIGN.md §10.12). */
export function takeFavor(run: NightRun, favor: FavorId): void {
  const times = run.favors.includes('double') ? 2 : 1;
  if (times === 2) remove(run.favors, 'double');
  for (let i = 0; i < times; i++) {
    switch (favor) {
      case 'starlight': {
        const rng = rngFor(run, `starlight:${run.night}:${run.table}:${i}`);
        const targets = [
          'ladder',
          'echo',
          'streak',
          'fullCircle',
          'capture',
          'crown',
          'landing',
          'none',
        ] as const;
        const t = targets[rng.nextBelow(targets.length)]!;
        run.appraisals[t] = Math.max(1, run.appraisals[t] ?? 1) + 3;
        break;
      }
      case 'speed':
        run.brass += 5 * (run.counters.skipped ?? 0);
        break;
      case 'mourning': {
        const rng = rngFor(run, `mourning:${run.night}:${run.table}:${i}`);
        const route = MOURNING_ROUTES[rng.nextBelow(MOURNING_ROUTES.length)]!;
        run.epitaphs.push({
          id: `M-${nextUid(run)}`,
          owner: 'S',
          sourcePawnId: route.id,
          inscription: route.inscription,
        });
        break;
      }
      default:
        run.favors.push(favor);
    }
  }
}

/** Skips the current Small or Big Table for its Favor (docs/SHOP_DESIGN.md §5.4). */
export function skipTable(profile: Profile): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  if (run.phase !== 'board') throw new CampaignError('no table is waiting');
  const table = currentTable(run);
  if (table.kind !== 'small' && table.kind !== 'big')
    throw new CampaignError('only Small and Big Tables can be skipped');
  table.status = 'skipped';
  run.counters.skipped = (run.counters.skipped ?? 0) + 1;
  run.outcomes.push({
    night: run.night,
    kind: table.kind,
    won: false,
    skipped: true,
    result: null,
    scores: { S: 0, N: 0 },
    ante: table.ante,
    brass: { table: 0, thrift: 0, interest: 0, items: 0, match: 0 },
    marked: [],
    lost: [],
    vanished: [],
    reclaimed: [],
    recruited: [],
    bestChain: 0,
  });
  if (table.favor) takeFavor(run, table.favor);
  // Word reaches the High Table: its head start grows for every table skipped on the way.
  const high = run.board.findIndex((t) => t.kind === 'high' && t.status === 'open');
  if (high >= 0) {
    const t = run.board[high]!;
    run.board[high] = { ...t, ante: Math.round(t.ante * (1 + LONG_NIGHT.SKIP_RAISE)) };
  }
  run.table += 1;
  return p;
}

/**
 * The head start this Night's High Table will open with (per round), as things stand, or if the
 * current table is skipped: the skip's rise first, then the Toll Waiver's halving when it will be
 * held by then (already, or as this skip's Favor), as enterTable applies them. Null when the
 * Night has no High Table left.
 */
export function highTableHeadStart(run: NightRun, skipping = false): number | null {
  const high = run.board.find((t) => t.kind === 'high' && t.status === 'open');
  if (!high) return null;
  let ante = high.ante;
  if (skipping) ante = Math.round(ante * (1 + LONG_NIGHT.SKIP_RAISE));
  const favor = skipping ? run.board[run.table]?.favor : undefined;
  if (run.favors.includes('toll-waiver') || favor === 'toll-waiver') ante = Math.floor(ante / 2);
  return ante;
}

/** Changes the pawns fielded on files A–F for the coming tables. */
export function setFielding(profile: Profile, fielding: readonly FieldedPawn[]): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  if (fielding.length !== 6) throw new CampaignError('field exactly six pawns');
  const ids = new Set(fielding.map((f) => f.id));
  const files = new Set(fielding.map((f) => f.file));
  if (ids.size !== 6 || files.size !== 6) throw new CampaignError('each pawn and file once');
  for (const f of fielding) {
    if (!Number.isInteger(f.file) || f.file < 0 || f.file > 5) throw new CampaignError('bad file');
    if (!run.roster.some((r) => r.id === f.id))
      throw new CampaignError(`${f.id} is not on the roster`);
  }
  const byFile: string[] = [];
  for (const f of fielding) byFile[f.file] = f.id;
  run.fielding = byFile;
  return p;
}

/** The difficulty of the Broker's side at a table. */
export function tableDifficulty(night: number, kind: TableKind): 'easy' | 'standard' | 'hard' {
  if (kind === 'closing' || night > LONG_NIGHT.NIGHTS + 1) return 'hard';
  if (kind === 'high') return night === 1 ? 'easy' : 'standard';
  return night === 1 ? 'easy' : 'standard';
}

const shuffledIndices = (n: number, rng: ReturnType<typeof rngFor>): number[] =>
  rng.shuffle(Array.from({ length: n }, (_, i) => i));

/** The Ash Pledge and Term VII: whether a Superstition rots (stops working after 5 tables). */
export const rots = (run: NightRun, uid: string): boolean =>
  run.counters.rotting === 1 || run.counters[`rotting:${uid}`] === 1;

export const isRotten = (run: NightRun, uid: string): boolean =>
  rots(run, uid) && (run.counters[`rot:${uid}`] ?? 0) >= 5;

/** Term IV: a Superstition On Loan costs ¤1 a table and sells for nothing. */
export const onLoan = (run: NightRun, uid: string): boolean => run.counters[`loan:${uid}`] === 1;

/** Term VII: a Bolted Superstition can't be sold or destroyed. */
export const isBolted = (run: NightRun, uid: string): boolean =>
  run.counters[`bolted:${uid}`] === 1;

function ownKit(run: NightRun): KitSpec {
  const tableCurio = (id: string) =>
    ![
      'whittling-knife',
      'ink-pot',
      'file',
      'rubber-stamp',
      'tracing-paper',
      'coin-purse',
      'appraisers-note',
    ].includes(id);
  return {
    mantel: run.mantel.filter((i) => !isRotten(run, i.uid)),
    curios: run.curios.filter((c) => tableCurio(c.id)),
    appraisals: { ...run.appraisals },
    discovered: [...run.discovered],
    brass: run.brass,
    counters: {
      chains: run.counters.chains ?? 0,
      pawned: run.counters.pawned ?? 0,
      epitaphsThisTable: 0,
    },
    bonuses: {
      handLimit: (owns(run, 'long-fingers') ? 1 : 0) + (owns(run, 'sleight-of-hand') ? 1 : 0),
      epitaphSlots: owns(run, 'grave-plot') ? 1 : 0,
      epitaphPlays: owns(run, 'family-crypt') ? 1 : 0,
    },
    slots: mantelSlots(run),
  };
}

function brokerKit(run: NightRun, table: NightTable): KitSpec {
  const ids: string[] = [];
  // A regular brings its signature Superstition from the second Night on.
  if (table.kind === 'high' && table.regular !== undefined && run.night >= 2)
    ids.push(REGULAR_KITS[table.regular]!.signature);
  if (table.patron && run.term >= 2) ids.push(...(PATRON_QUIRKS[table.patron].mantel ?? []));
  if (table.kind === 'closing' || run.night > LONG_NIGHT.NIGHTS + 1) {
    const rng = rngFor(run, `broker-kit:${run.night}`);
    const pool = SUPERSTITIONS.filter(
      (id) =>
        SUPERSTITION_INFO[id].rarity !== 'legendary' &&
        !ECONOMY_SUPERSTITIONS.includes(id) &&
        id !== 'brokers-monocle',
    );
    for (let i = 0; i < (table.kind === 'closing' ? 3 : 1); i++)
      ids.push(pool[rng.nextBelow(pool.length)]!);
  }
  return {
    mantel: ids.map((id, i) => ({ uid: `broker-${i}`, id })),
    curios: [],
    appraisals: {},
    discovered: [],
    brass: 0,
    counters: {},
    bonuses: {},
  };
}

/** Builds the current table's match (docs/SHOP_DESIGN.md §5.2) and sits down at it. */
export function fieldNight(profile: Profile, constants: Constants = DEFAULT_CONSTANTS): Profile {
  const p = clone(profile);
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  if (run.phase !== 'board') throw new CampaignError('no table is waiting');
  const table = currentTable(run);
  const fielded = run.fielding.filter((id) => run.roster.some((r) => r.id === id));
  if (fielded.length !== 6) {
    run.fielding = run.roster.slice(0, 6).map((r) => r.id);
    fielded.splice(0, fielded.length, ...run.fielding);
  }
  const oneRound = table.kind === 'small' || table.kind === 'big';
  const rng = rngFor(run, `match:${run.night}:${run.table}`);
  const seed = hex64(rng.nextU64());

  // South: the cut of the run deck at a one-round table, else the whole deck.
  let cut: number[] | null = null;
  let deckS = run.deck;
  if (oneRound) {
    const bonus =
      (owns(run, 'deep-pockets') ? 2 : 0) +
      (owns(run, 'bottomless-pockets') ? 2 : 0) +
      (run.pledge === 'bare-box' ? 2 : 0) +
      (run.favors.includes('handful') ? 3 : 0);
    if (run.favors.includes('handful')) remove(run.favors, 'handful');
    const n = Math.min(run.deck.length, LONG_NIGHT.CUT + bonus);
    cut = shuffledIndices(run.deck.length, rngFor(run, `cut:${run.night}:${run.table}`)).slice(
      0,
      n,
    );
    deckS = cut.map((i) => run.deck[i]!);
  }
  if (deckS.length === 0) throw new CampaignError('the run deck is empty');
  // North: the base deck, cut at one-round tables.
  let deckN = baseDeckSpec(constants);
  if (oneRound) {
    const n = run.night === 1 ? LONG_NIGHT.CUT_PATRON_NIGHT1 : LONG_NIGHT.CUT;
    deckN = shuffledIndices(deckN.length, rngFor(run, `cutN:${run.night}:${run.table}`))
      .slice(0, n)
      .map((i) => deckN[i]!);
  }

  // Pawns: South's fielded roster with its Carvings; North's own, and former pawns.
  const standIns = new Set(run.roster.filter((x) => x.standIn).map((x) => x.id));
  const hollow =
    (table.kind === 'high' || table.kind === 'closing') && run.counters.hollowCrown === 1;
  const carvingsOf = (id: string): CarvingSpec[] => {
    const list = [...(run.carvings[id] ?? [])];
    if (hollow && !list.some((c) => c.id === 'crowned-head')) list.push({ id: 'crowned-head' });
    return list;
  };
  const south: PawnSpec[] = run.fielding.map((id, file) => {
    const carvings = carvingsOf(id);
    return {
      id,
      file,
      ...(standIns.has(id) ? { standIn: true } : { name: p.pawns[id]?.name ?? id }),
      ...(carvings.length ? { carvings } : {}),
    };
  });
  const maxFormers = table.kind === 'closing' ? 6 : constants.CAMPAIGN_FORMER_PAWNS_MAX;
  const k = Math.min(maxFormers, p.collection.length);
  const pawnRng = rngFor(run, `pawns:${run.night}:${run.table}`);
  const formers = pawnRng.shuffle([...p.collection]).slice(0, k);
  const northFiles = pawnRng.shuffle([0, 1, 2, 3, 4, 5]);
  const north: PawnSpec[] = northFiles
    .map((file, i) => {
      const former = formers[i];
      if (!former) return { id: `N-${FILES[file]}`, file };
      const carvings = [...(l.formerCarvings[former] ?? [])];
      const hallmark = l.hallmarks[former];
      if (hallmark && !carvings.some((c) => c.id === hallmark.id)) carvings.push(hallmark);
      return {
        id: former,
        file,
        name: p.pawns[former]?.name ?? former,
        former: true,
        ...(carvings.length ? { carvings } : {}),
      };
    })
    .sort((a, b) => a.file - b.file);
  const reserves: ReserveSpec[] = run.roster
    .filter((x) => !run.fielding.includes(x.id))
    .map((x) =>
      x.standIn ? { id: x.id, standIn: true } : { id: x.id, name: p.pawns[x.id]?.name ?? x.id },
    );

  // The head start: Favors, the Black Wild, and the Broker's Final Appraisal.
  let ante = table.ante;
  if ((table.kind === 'high' || table.kind === 'closing') && run.favors.includes('toll-waiver')) {
    ante = Math.floor(ante / 2);
    remove(run.favors, 'toll-waiver');
  }
  if (run.counters.blackWild) {
    ante = Math.round(ante * 1.5);
    delete run.counters.blackWild;
  }
  if (table.kind === 'closing' && run.brokerRule === 'final-appraisal')
    ante = Math.max(ante, 2 * run.bestChain);

  const rules: HouseRuleSpec[] = [...table.rules];
  if (run.favors.includes('ward')) {
    rules.push({ id: 'warded-start', side: 'S' });
    remove(run.favors, 'ward');
  }
  const omens = [...table.omens];
  for (const o of run.pendingOmens)
    if (!omens.some((x) => x.square.x === o.square.x && x.square.y === o.square.y)) omens.push(o);
  if (table.patron && run.term >= 2)
    for (const kind of PATRON_QUIRKS[table.patron].omens ?? []) {
      const sq = { x: pawnRng.nextBelow(6), y: 1 + pawnRng.nextBelow(4) };
      if (!omens.some((x) => x.square.x === sq.x && x.square.y === sq.y))
        omens.push({ kind, square: sq, shrouded: true });
    }
  const startEpitaphs: Epitaph[] = run.epitaphs.map((e, i) => ({
    ...e,
    id: `K${run.night}-${run.table}-${i}`,
    owner: 'S',
  }));

  const config = makeConfig({
    mode: 'campaign',
    preset: constants.DEFAULT_PRESET,
    northHeadStart: Math.min(1e15, ante),
    constants: oneRound ? { ...constants, MAX_ROUNDS: 1, ROUND_WINS_NEEDED: 1 } : constants,
    seating: { players: ['player', `broker-${run.night}-${run.table}`], assignment: 'fixed' },
    decks: { S: deckS, N: deckN },
    pawns: { S: south, N: north },
    reserves: { S: reserves },
    ledger: {
      kits: { S: ownKit(run), N: brokerKit(run, table) },
      omens: omens.slice(0, 24),
      houseRules: rules.slice(0, 12),
      anteBlind: true,
      ...(startEpitaphs.length ? { startEpitaphs: { S: startEpitaphs } } : {}),
    },
  });
  run.pendingOmens = [];
  run.lastMatch = null;
  run.match = {
    config,
    seed,
    actions: [],
    kind: table.kind,
    night: run.night,
    formerPawns: formers,
    fielded: [...run.fielding],
    cut,
  };
  run.phase = 'playing';
  for (const item of run.mantel) if (!l.seen.includes(item.id)) l.seen.push(item.id);
  return p;
}

/** Appends an applied action to the table's replay, so the match can be resumed (§16.5). */
export function recordNightAction(
  profile: Profile,
  action: NightMatch['actions'][number],
): Profile {
  const run = profile.ledger?.run;
  if (!run || run.phase !== 'playing' || !run.match)
    throw new CampaignError('no table is being played');
  return {
    ...profile,
    ledger: {
      ...profile.ledger!,
      run: { ...run, match: { ...run.match, actions: [...run.match.actions, action] } },
    },
  };
}

// ---------------------------------------------------------------------------------------------
// The end of a table
// ---------------------------------------------------------------------------------------------

/** Interest on the brass held (docs/SHOP_DESIGN.md §6.1). */
export function interestOn(run: NightRun): number {
  if (run.brass <= 0) return 0;
  const golden = run.mantel.some((i) => i.id === 'golden-tooth');
  const cap = golden
    ? Infinity
    : owns(run, 'vault-door')
      ? 20
      : owns(run, 'strongbox')
        ? 10
        : LONG_NIGHT.INTEREST_CAP;
  return Math.min(cap, Math.floor(run.brass / LONG_NIGHT.INTEREST_STEP));
}

/** Brings a finished table's changes back to the run deck: Stamps, values, shattered cards, IOUs. */
function updateDeck(run: NightRun, final: MatchState): void {
  const match = run.match as NightMatch;
  const l = final.ledger;
  if (!l) return;
  const index = cardSpecIndex(match.config, 'S');
  const runIndex = (cardId: string): number | null => {
    const i = index[cardId];
    if (i === undefined) return null;
    return match.cut ? (match.cut[i] ?? null) : i;
  };
  for (const [cardId, stamp] of Object.entries(l.cardStamps)) {
    const i = runIndex(cardId);
    if (i !== null && run.deck[i]) run.deck[i] = { ...run.deck[i]!, stamp };
  }
  for (const [cardId, value] of Object.entries(l.cardValues)) {
    const i = runIndex(cardId);
    if (i !== null && run.deck[i]) run.deck[i] = { ...run.deck[i]!, value };
  }
  const gone = new Set<number>();
  for (const cardId of l.shattered) {
    const i = runIndex(cardId);
    if (i !== null) gone.add(i);
  }
  // IOUs Recovered at the table are gone for the run.
  for (const cardId of final.players.S.removed) {
    if (!final.cards[cardId]?.iou) continue;
    const i = runIndex(cardId);
    if (i !== null) gone.add(i);
  }
  if (gone.size) run.deck = run.deck.filter((_, i) => !gone.has(i));
}

/** Everything the run needs from a finished Long Night table. */
export interface NightTableResult {
  readonly final: MatchState;
  readonly events: readonly MatchEvent[];
}

/** Resolves a finished table: brass, items, cards, pawns; then the Counter, Spoils or the end. */
export function finishNightTable(
  profile: Profile,
  res: NightTableResult,
  constants: Constants = DEFAULT_CONSTANTS,
): Profile {
  const run0 = profile.ledger?.run;
  if (!run0 || run0.phase !== 'playing' || !run0.match)
    throw new CampaignError('no table is being played');
  const p = clone(profile);
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  const match = run.match as NightMatch;
  const table = currentTable(run);
  const final = res.final;
  const result = final.result;
  if (!result) throw new CampaignError('the table is not over');
  const won = result.winner === 'S';
  const ledger = final.ledger;

  applyRecords(p, res.events);
  for (const id of [...match.fielded, ...match.formerPawns]) {
    const r = p.pawns[id]?.records;
    if (r) r.tablesPlayed += 1;
  }
  // Items, brass and counters come back from the table.
  const matchBrass = ledger ? ledger.brass.S - run.brass : 0;
  if (ledger) {
    run.brass = ledger.brass.S;
    const lostUids = new Set(ledger.lost.S);
    const back = new Map(ledger.mantel.S.map((i) => [i.uid, i]));
    run.mantel = run.mantel.filter((i) => !lostUids.has(i.uid)).map((i) => back.get(i.uid) ?? i);
    const kept = new Set(ledger.curios.S.map((c) => c.uid));
    const tableIds = new Set(match.config.ledger?.kits.S.curios.map((c) => c.uid));
    run.curios = run.curios.filter((c) => !tableIds.has(c.uid) || kept.has(c.uid));
    run.counters.chains = ledger.counters.S.chains ?? run.counters.chains ?? 0;
    run.counters.pawned = ledger.counters.S.pawned ?? run.counters.pawned ?? 0;
    for (const [key, value] of Object.entries(ledger.pawnValues)) {
      const [pawnId, carving] = key.split(':');
      const list = run.carvings[pawnId!];
      if (list) run.carvings[pawnId!] = list.map((c) => (c.id === carving ? { ...c, value } : c));
    }
    // How often each of your Superstitions fired, for the run's summary (§11.21 of the art
    // direction): counted by kind, so one lost since still counts.
    for (const item of match.config.ledger?.kits.S.mantel ?? []) {
      const n = ledger.triggers[item.uid] ?? 0;
      if (n > 0) run.counters[`fired:${item.id}`] = (run.counters[`fired:${item.id}`] ?? 0) + n;
    }
    updateDeck(run, final);
    const keepsake = run.mantel.some((i) => i.id === 'keepsake-locket');
    run.epitaphs = keepsake ? (ledger.finalEpitaphs?.S ?? []).map((e) => ({ ...e })) : [];
  }
  let best = 0;
  // The links of the chain being played, to keep the run's best one for its share card.
  let steps: RouteStep[] = [];
  for (const e of res.events) {
    if (e.type === 'TurnStarted') steps = [];
    if (e.type === 'LinkPlayed' && e.side === 'S') steps.push(e.step);
    if (e.type === 'ChainScored') {
      if (e.side === 'S' && e.breakdown.score > run.bestChain && e.breakdown.score > best) {
        run.bestKeepsake = {
          score: e.breakdown.score,
          points: e.breakdown.points.total,
          multiplier: e.breakdown.multiplier.total,
          path: steps.length ? [steps[0]!.from, ...steps.flatMap((x) => x.landings)] : [],
          faces: steps.map((x) => x.face),
          night: run.night,
          kind: table.kind,
          ...(table.regular !== undefined ? { regular: table.regular } : {}),
        };
      }
      steps = [];
    }
    if (e.type === 'ChainScored' && e.side === 'S') best = Math.max(best, e.breakdown.score);
    if (e.type === 'InscriptionSet' && e.side === 'S') {
      const prior = run.bestInscriptions[e.pawnId];
      if (!prior || e.inscription.recordedScore > prior.recordedScore)
        run.bestInscriptions[e.pawnId] = e.inscription;
    }
  }
  run.bestChain = Math.max(run.bestChain, best);
  noteStats(l, run, res.events, final);

  const onRoster = new Set(run.roster.map((r) => r.id));
  const marked = [...new Set(final.players.S.collateralUsed)].filter((id) => onRoster.has(id));
  const isStandIn = (id: string) => run.roster.some((r) => r.id === id && r.standIn);
  const base = {
    night: run.night,
    kind: table.kind,
    skipped: false,
    result,
    scores: { S: final.players.S.totalScore, N: final.players.N.totalScore },
    ante: match.config.northHeadStart,
    marked,
    bestChain: best,
  };
  // Kept until the next table: the Counter recounts its best chain with an item added.
  run.lastMatch = match;
  run.match = null;
  if (!won) {
    // Lost: marked named pawns go to the Broker with their Carvings; the run ends (§15.5).
    const lostIds = marked.filter((id) => !isStandIn(id));
    const vanished = marked.filter(isStandIn);
    if (run.term >= 8)
      for (const id of match.fielded) if (!isStandIn(id) && !lostIds.includes(id)) lostIds.push(id);
    for (const id of lostIds) {
      remove(p.stable, id);
      if (!p.collection.includes(id)) p.collection.push(id);
      const carvings = run.carvings[id];
      if (carvings?.length) l.formerCarvings[id] = carvings.map((c) => ({ ...c }));
      const ins = run.bestInscriptions[id];
      if (ins && !l.heirlooms.some((h) => h.pawnId === id && h.inscription === ins))
        l.heirlooms.push({
          id: `${run.number}-${id}`,
          pawnId: id,
          name: p.pawns[id]?.name ?? id,
          inscription: ins,
        });
    }
    if (l.heirlooms.length > 12) l.heirlooms.splice(0, l.heirlooms.length - 12);
    run.roster = run.roster.filter((r) => !lostIds.includes(r.id) && !vanished.includes(r.id));
    table.status = 'lost';
    run.outcomes.push({
      ...base,
      won: false,
      brass: { table: 0, thrift: 0, interest: 0, items: 0, match: matchBrass },
      lost: lostIds,
      vanished,
      reclaimed: [],
      recruited: [],
    } satisfies NightOutcome);
    run.phase = 'lost';
    checkUnlocks(p);
    return p;
  }

  // Won: brass for the table, Thrift and interest (§6.1).
  table.status = 'won';
  const kindIndex = table.kind === 'small' ? 0 : table.kind === 'big' ? 1 : 2;
  let tableBrass: number = LONG_NIGHT.BRASS_TABLE[kindIndex];
  if (table.kind === 'big' && run.term >= 5) tableBrass -= 1;
  const miser = run.mantel.filter((i) => i.id === 'misers-purse').length;
  const oneRound = table.kind === 'small' || table.kind === 'big';
  const thriftRaw = oneRound
    ? Math.min(LONG_NIGHT.THRIFT_ONE_ROUND_MAX, final.players.S.hand.length)
    : Math.min(
        LONG_NIGHT.THRIFT_HIGH_MAX,
        Math.floor((final.players.S.hand.length + final.players.S.deck.length) / 3),
      );
  const thrift = thriftRaw * 2 ** miser;
  const interest = interestOn(run);
  let items = 3 * run.mantel.filter((i) => i.id === 'coin-in-the-shoe').length;
  if (run.favors.includes('bounty')) {
    items += 2 * res.events.filter((e) => e.type === 'PawnCaptured' && e.side === 'N').length;
    remove(run.favors, 'bounty');
  }
  if ((table.kind === 'high' || table.kind === 'closing') && run.favors.includes('investment')) {
    items += 20;
    remove(run.favors, 'investment');
  }
  // On Loan items cost ¤1 a table; Rotting items rot; Piggy Banks grow.
  run.mantel = run.mantel.map((item) =>
    item.id === 'piggy-bank' ? { ...item, value: (item.value ?? 0) + 2 } : item,
  );
  for (const item of run.mantel)
    if (rots(run, item.uid))
      run.counters[`rot:${item.uid}`] = (run.counters[`rot:${item.uid}`] ?? 0) + 1;
  items -= run.mantel.filter((i) => onLoan(run, i.uid)).length;
  run.brass += tableBrass + thrift + interest + items;
  l.stats.mostBrass = Math.max(l.stats.mostBrass, run.brass);

  const reclaimed: string[] = [];
  for (const id of match.formerPawns) {
    if (!p.collection.includes(id)) continue;
    remove(p.collection, id);
    p.stable.push(id);
    reclaimed.push(id);
    const named = p.pawns[id];
    if (named) named.homecomings = (named.homecomings ?? 0) + 1;
    delete l.formerCarvings[id];
    const slot = run.roster.findIndex((r) => r.standIn);
    if (slot >= 0) run.roster[slot] = { id, standIn: false };
    else run.roster.push({ id, standIn: false });
  }
  const recruited: string[] = [];
  if (table.kind === 'high' || table.kind === 'closing') {
    const n = constants.CAMPAIGN_RECRUITS_PER_WIN + (owns(run, 'guild-hall') ? 1 : 0);
    for (let i = 0; i < n; i++) {
      const pawn = recruitInto(p, 10_000 + run.number);
      if (!pawn.records) pawn.records = emptyRecords();
      recruited.push(pawn.id);
      run.roster.push({ id: pawn.id, standIn: false });
    }
  }
  run.outcomes.push({
    ...base,
    won: true,
    brass: { table: tableBrass, thrift, interest, items, match: matchBrass },
    lost: [],
    vanished: [],
    reclaimed,
    recruited,
  } satisfies NightOutcome);
  checkUnlocks(p);

  if (table.kind === 'closing') {
    run.phase = 'won';
    l.stats.wins += 1;
    if (!run.daily) l.terms[run.pledge] = Math.max(l.terms[run.pledge] ?? 0, run.term);
    if (run.term >= 3) l.hallmarkPending = true;
    checkUnlocks(p);
    return p;
  }
  if (table.kind === 'high' && run.night > LONG_NIGHT.NIGHTS + 1) {
    // After Hours: another Hour begins after the Counter.
    run.table += 1;
    openCounter(p);
    return p;
  }
  if (table.kind === 'high') {
    enterSpoils(p);
    return p;
  }
  run.table += 1;
  openCounter(p);
  return p;
}

/** After the Counter: the next table, or the next Night (and its board). */
export function advanceAfterCounter(p: Profile): void {
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  if (run.table < run.board.length) {
    run.phase = 'board';
    return;
  }
  enterNight(run, run.night + 1, l);
}

/** Goes on into After Hours after winning Closing Time (docs/SHOP_DESIGN.md §14.6). */
export function continueAfterHours(profile: Profile): Profile {
  const p = clone(profile);
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  if (run.phase !== 'won') throw new CampaignError('only a won run goes on into After Hours');
  if (!l.unlocked.includes('after-hours')) throw new CampaignError('After Hours is not open yet');
  enterNight(run, run.night + 1, l);
  return p;
}

function finishNightRunInto(l: LedgerProfile, abandoned: boolean): void {
  const run = l.run;
  if (!run) return;
  const won = run.outcomes.some((o) => o.kind === 'closing' && o.won);
  l.history.push({
    number: run.number,
    pledge: run.pledge,
    term: run.term,
    won,
    night: run.night,
    bestChain: run.bestChain,
    abandoned,
    ...(run.daily ? { daily: run.daily } : {}),
  });
  if (l.history.length > 100) l.history.splice(0, l.history.length - 100);
  l.stats.bestChain = Math.max(l.stats.bestChain, run.bestChain);
  if (run.daily) {
    const prior = l.daily[run.daily];
    if (
      !prior ||
      run.night > prior.night ||
      (run.night === prior.night && run.bestChain > prior.bestChain)
    )
      l.daily[run.daily] = { night: run.night, bestChain: run.bestChain, won };
  }
  l.run = null;
  l.hallmarkPending = false;
}

/** Closes a finished run after its summary (won, lost, or ending After Hours). */
export function closeNightRun(profile: Profile): Profile {
  const p = clone(profile);
  const l = ledgerOf(p);
  const run = l.run;
  if (!run) return p;
  if (run.phase !== 'won' && run.phase !== 'lost')
    throw new CampaignError('the run is still in progress');
  finishNightRunInto(l, false);
  return p;
}

/** Abandons a run between tables; it counts as lost (marked pawns are safe: none are marked then). */
export function abandonNightRun(profile: Profile): Profile {
  const p = clone(profile);
  const l = ledgerOf(p);
  const run = l.run;
  if (!run) return p;
  if (run.phase === 'playing') throw new CampaignError('concede the current table first');
  finishNightRunInto(l, run.phase !== 'won' && run.phase !== 'lost');
  return p;
}

/** Makes one of a pawn's Carvings a Hallmark after a run won at Term III or higher (§14.3). */
export function setHallmark(
  profile: Profile,
  pawnId: string | null,
  carving: string | null,
): Profile {
  const p = clone(profile);
  const l = ledgerOf(p);
  const run = l.run;
  if (!l.hallmarkPending || !run) throw new CampaignError('no Hallmark is waiting');
  if (pawnId && carving) {
    const highs = run.outcomes.filter((o) => o.kind === 'high' || o.kind === 'closing');
    void highs;
    const c = (run.carvings[pawnId] ?? []).find((x) => x.id === carving);
    if (!c || !p.stable.includes(pawnId)) throw new CampaignError('that pawn has no such Carving');
    l.hallmarks[pawnId] = { id: c.id };
  }
  l.hallmarkPending = false;
  return p;
}

/** Ids of roster pawns that may take a Hallmark: named, still in the stable, with a Carving. */
export function hallmarkCandidates(p: Profile): string[] {
  const run = p.ledger?.run;
  if (!run) return [];
  return run.roster
    .filter((r) => !r.standIn && p.stable.includes(r.id) && (run.carvings[r.id] ?? []).length > 0)
    .map((r) => r.id);
}
